import { FC, useState } from 'react';
import { graphql, useFragment } from 'react-relay';
import classNames from 'classnames';
import dibsCss from 'dibs-css';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { Spinner } from 'dibs-elements/exports/Spinner';
import { vatMessages } from 'dibs-vat-utils/exports/vatMessages';
import CheckoutSubmitButton from '../components/containers/CheckoutSubmitButton';

import { CheckoutStickySubmitButton_viewer$key } from './__generated__/CheckoutStickySubmitButton_viewer.graphql';
import { CheckoutStickySubmitButton_item$key } from './__generated__/CheckoutStickySubmitButton_item.graphql';
import { CheckoutStickySubmitButton_buyer$key } from './__generated__/CheckoutStickySubmitButton_buyer.graphql';
import { CheckoutStickySubmitButton_transaction$key } from './__generated__/CheckoutStickySubmitButton_transaction.graphql';

const viewerFragment = graphql`
    fragment CheckoutStickySubmitButton_viewer on Viewer {
        ...CheckoutSubmitButton_viewer
    }
`;

const itemFragment = graphql`
    fragment CheckoutStickySubmitButton_item on Item {
        ...CheckoutSubmitButton_item
    }
`;

const buyerFragment = graphql`
    fragment CheckoutStickySubmitButton_buyer on User {
        ...CheckoutSubmitButton_buyer
    }
`;

const transactionFragment = graphql`
    fragment CheckoutStickySubmitButton_transaction on Transaction {
        ...CheckoutSubmitButton_transaction
        buyerPriceTables(displayCurrency: $displayCurrency) {
            tables {
                type
                rows {
                    rowType
                    label
                    value
                }
            }
        }
        vatAmount {
            amount
        }
        priceBookName
    }
`;

type Props = {
    viewer: CheckoutStickySubmitButton_viewer$key;
    item: CheckoutStickySubmitButton_item$key;
    buyer: CheckoutStickySubmitButton_buyer$key;
    transaction: CheckoutStickySubmitButton_transaction$key;
    isComputing: boolean;
    isLoading: boolean;
    isDisabled: boolean;
};

export const CheckoutStickySubmitButton: FC<Props> = props => {
    const {
        viewer: viewerRef,
        item: itemRef,
        buyer: buyerRef,
        transaction: transactionRef,
        isComputing,
        isLoading,
        isDisabled,
    } = props;

    const [isSticking, setIsSticking] = useState(false);

    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const buyer = useFragment(buyerFragment, buyerRef);
    const transaction = useFragment(transactionFragment, transactionRef);

    const priceRows =
        transaction?.buyerPriceTables?.tables?.find(table => table?.type === 'MAIN')?.rows || [];
    const totalRow = priceRows.find(row => row?.rowType === 'TOTAL');
    const priceBookName = transaction?.priceBookName;

    return (
        <>
            <div
                className={classNames(dibsCss.sticky, dibsCss.bottom0, dibsCss.pbMedium, {
                    [dibsCss.borderT]: isSticking,
                    [dibsCss.borderTSolid]: isSticking,
                    [dibsCss.borderTDolphin]: isSticking,
                    [dibsCss.bgWhite]: isSticking,
                })}
            >
                {isSticking && (
                    <div className={classNames(dibsCss.ptXsmall, dibsCss.pbXsmall)}>
                        <div
                            className={classNames(
                                dibsCss.flex,
                                dibsCss.justifyBetween,
                                dibsCss.sassyFontBodyTypeHeavy
                            )}
                        >
                            <span>{totalRow?.label}</span>
                            <span>
                                {isComputing && (
                                    <span
                                        className={classNames(
                                            dibsCss.inlineBlock,
                                            dibsCss.mrXsmall,
                                            dibsCss.alignTextTop
                                        )}
                                    >
                                        <Spinner size="tiny" />
                                    </span>
                                )}
                                {totalRow?.value}
                            </span>
                        </div>
                        {!!transaction?.vatAmount?.amount && (
                            <div
                                className={classNames(
                                    dibsCss.sassyFontBodySizeSmall,
                                    dibsCss.textSatan,
                                    dibsCss.textRight
                                )}
                            >
                                {vatMessages.included({ priceBookName })}
                            </div>
                        )}
                    </div>
                )}
                <CheckoutSubmitButton
                    viewer={viewer}
                    item={item}
                    buyer={buyer}
                    transaction={transaction}
                    isComputing={isComputing}
                    isLoading={isLoading}
                    isDisabled={isDisabled}
                />
            </div>
            <VisibilityTracker
                watchAfterFirstVisible
                onVisibilityChange={({ isVisible }) => setIsSticking(!isVisible)}
            />
        </>
    );
};
